import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { brl } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import {
  Wallet, TrendingUp, TrendingDown, Scale, ArrowDownCircle, ArrowUpCircle,
  CalendarClock, AlertTriangle, CalendarDays, Loader2,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const COLORS = ["#123826", "#C5A059", "#0284C7", "#D97706", "#7C3AED", "#059669", "#DB2777", "#475569"];

function Kpi({ title, value, icon: Icon, accent, testId, positive }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden" data-testid={testId}>
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand to-gold" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{title}</div>
          <div className={`text-2xl sm:text-[26px] font-bold font-mono tracking-tight ${positive === false ? "text-rose-600" : positive === true ? "text-emerald-700" : "text-slate-900"}`}>
            {value}
          </div>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function DueCard({ title, icon: Icon, data, testId }) {
  const rows = [
    { label: "Hoje", value: data.hoje, icon: CalendarDays, cls: "text-blue-700" },
    { label: "Próximos 7 dias", value: data.proximos_7_dias, icon: CalendarClock, cls: "text-amber-700" },
    { label: "Vencidas", value: data.vencidas, icon: AlertTriangle, cls: "text-rose-700" },
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-brand" />
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
            <span className="flex items-center gap-2 text-sm text-slate-600"><r.icon className={`h-4 w-4 ${r.cls}`} />{r.label}</span>
            <span className={`font-mono font-semibold text-sm ${r.cls}`}>{brl(r.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const money = (v) => "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function MonthDetailModal({ ym, label, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!ym) return;
    setLoading(true);
    api.get(`/dashboard/month-transactions?ym=${ym}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [ym]);
  return (
    <Dialog open={!!ym} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="month-detail-modal">
        <DialogHeader>
          <DialogTitle>Movimentações de {label}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : !data || data.items.length === 0 ? (
          <EmptyState text="Nenhuma movimentação neste mês." />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-emerald-50 p-3">
                <div className="text-xs font-semibold uppercase text-emerald-700">Receitas</div>
                <div className="font-mono font-bold text-emerald-700">{brl(data.total_receitas)}</div>
              </div>
              <div className="rounded-lg bg-gold-light p-3">
                <div className="text-xs font-semibold uppercase text-gold">Despesas</div>
                <div className="font-mono font-bold text-gold">{brl(data.total_despesas)}</div>
              </div>
            </div>
            <div className="max-h-[50vh] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="month-detail-list">
              {data.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="text-slate-800 truncate">{it.description}</div>
                    <div className="text-xs text-slate-400">{fmtDate(it.date)}{it.cost_center_name ? ` • ${it.cost_center_name}` : ""}</div>
                  </div>
                  <div className={`font-mono font-semibold whitespace-nowrap ${it.type === "entrada" ? "text-emerald-700" : "text-gold"}`}>
                    {it.type === "entrada" ? "+" : "−"} {brl(it.amount)}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-400 text-right">{data.count} movimentação(ões)</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rdMonths, setRdMonths] = useState(6);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (!data) return <EmptyState />;

  const hasRD = data.grafico_receitas_despesas.some((m) => m.receitas || m.despesas);
  const rdData = data.grafico_receitas_despesas.slice(-rdMonths);
  const hasCC = data.grafico_despesas_centro_custo.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard Financeiro"
        subtitle="Visão geral da situação financeira do Clube Monte Líbano"
        actions={
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 shadow-sm" data-testid="filter-date-range-picker">
            <CalendarDays className="h-4 w-4 text-brand" /> Mês atual
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi title="Saldo Disponível" value={brl(data.saldo_disponivel)} icon={Wallet} accent="bg-brand-light text-brand" testId="kpi-saldo-disponivel-value" />
        <Kpi title="Receitas do Mês" value={brl(data.receitas_mes)} icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" testId="kpi-receitas-mes-value" />
        <Kpi title="Despesas do Mês" value={brl(data.despesas_mes)} icon={TrendingDown} accent="bg-rose-50 text-rose-600" testId="kpi-despesas-mes-value" />
        <Kpi title="Resultado do Mês" value={brl(data.resultado_mes)} icon={Scale} accent="bg-gold-light text-gold" testId="kpi-resultado-mes-value" positive={data.resultado_mes >= 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DueCard title="Contas a Pagar" icon={ArrowDownCircle} data={data.contas_pagar} testId="dashboard-contas-pagar-card" />
        <DueCard title="Contas a Receber" icon={ArrowUpCircle} data={data.contas_receber} testId="dashboard-contas-receber-card" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="chart-receitas-despesas">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-slate-900">Receitas x Despesas <span className="text-xs font-normal text-slate-400">(clique numa barra para detalhar • últimos {rdMonths} meses)</span></h3>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="rd-months-filter">
              {[3, 6, 12].map((m) => (
                <button key={m} onClick={() => setRdMonths(m)} data-testid={`rd-months-${m}`} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${rdMonths === m ? "bg-white text-brand shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{m}m</button>
              ))}
            </div>
          </div>
          {rdData.some((x) => x.receitas || x.despesas) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={rdData} barGap={6} onClick={(e) => { const p = e?.activePayload?.[0]?.payload; if (p?.ym) setDetail({ ym: p.ym, label: p.label }); }} className="cursor-pointer">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={(v) => brl(v)} contentStyle={{ borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="receitas" name="Receitas" fill="#123826" radius={[4, 4, 0, 0]} />
                <Bar dataKey="despesas" name="Despesas" fill="#C5A059" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="chart-despesas-centro-custo">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Despesas por Centro de Custo</h3>
          {hasCC ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.grafico_despesas_centro_custo} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}>
                  {data.grafico_despesas_centro_custo.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => brl(v)} contentStyle={{ borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="chart-fluxo-financeiro">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Fluxo Financeiro <span className="text-xs font-normal text-slate-400">(entradas e saídas)</span></h3>
        {hasRD ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.grafico_fluxo.slice(-6)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={70} />
              <Tooltip formatter={(v) => brl(v)} contentStyle={{ borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="receitas" name="Entradas" stroke="#123826" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="despesas" name="Saídas" stroke="#C5A059" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="saldo" name="Resultado" stroke="#0284C7" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <EmptyState />}
      </div>

      {detail && <MonthDetailModal ym={detail.ym} label={detail.label} onClose={() => setDetail(null)} />}
    </div>
  );
}
